// FileIntentBridge — Android "Open with…" handoff.
//
// When the user taps a .csv in their phone's file manager and picks OpenCSV,
// the native MainActivity reads the file and fires a `opencsv:incoming-file`
// CustomEvent with `{ name, content }`. We catch it here, stash it in
// sessionStorage, and route to the editor which picks it up on mount.
//
// Pure side-effects component — render nothing.

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const STASH_KEY = "opencsv:pending-incoming-file";
const EVENT_NAME = "opencsv:incoming-file";

export default function FileIntentBridge() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onIncoming = (e) => {
            const detail = e?.detail;
            if (!detail || !detail.content) return;
            try {
                sessionStorage.setItem(STASH_KEY, JSON.stringify({
                    name: detail.name || "incoming.csv",
                    content: detail.content,
                }));
            } catch {/* quota or private mode — fall through */ }
            // Editor route reads from sessionStorage on mount
            if (!location.pathname.startsWith("/app")) {
                navigate("/app", { replace: false });
            } else {
                // Already in editor — nudge it to refresh
                window.dispatchEvent(new CustomEvent("opencsv:replay-incoming-file"));
            }
        };
        window.addEventListener(EVENT_NAME, onIncoming);
        return () => window.removeEventListener(EVENT_NAME, onIncoming);
    }, [navigate, location.pathname]);

    return null;
}

// Helper for CsvEditor to pull a stashed incoming file (if any).
export function consumePendingIncomingFile() {
    try {
        const raw = sessionStorage.getItem(STASH_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(STASH_KEY);
        return JSON.parse(raw);
    } catch {
        return null;
    }
}
